import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { SEGMENTS, VALID_PRICES, PRICE_TO_RATE } from '../../data/conjointData';
import { formatPrice } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS, LEGEND_PROPS } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

interface Props {
  selectedPrice?: number;
}

export function SegmentPriceCurves({ selectedPrice }: Props) {
  const isMobile = useIsMobile();
  const data = VALID_PRICES.map(price => {
    const row: Record<string, number> = { price };
    SEGMENTS.forEach(seg => { row[seg.shortName] = (seg.priceCurve[price] ?? 0) * 100; });
    row['Overall'] = (PRICE_TO_RATE[price] ?? 0) * 100;
    return row;
  });

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Segment Price Curves</h3>
        <InfoButton helpKey="segmentPriceCurves" size={13} />
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 220 : 340}>
        <LineChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="price" {...X_AXIS_PROPS} tickFormatter={v => formatPrice(v)} />
          <YAxis {...Y_AXIS_PROPS} tickFormatter={v => `${v}%`} domain={[0, 'auto']} />
          <Tooltip {...TOOLTIP_STYLE} labelFormatter={v => formatPrice(Number(v))} formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]} />
          <Legend {...LEGEND_PROPS} />
          {selectedPrice && (
            <ReferenceLine
              x={selectedPrice}
              stroke={CHART_COLORS.purple}
              strokeDasharray="6 3"
              strokeWidth={2}
              label={{
                value: formatPrice(selectedPrice),
                fill: CHART_COLORS.purple,
                fontSize: 11,
                fontWeight: 700,
                position: 'top',
              }}
            />
          )}
          {SEGMENTS.map(seg => (
            <Line key={seg.shortName} type="monotone" dataKey={seg.shortName} stroke={seg.color} strokeWidth={2} dot={{ r: 4, fill: seg.color, stroke: 'var(--color-dot-stroke)', strokeWidth: 2 }} />
          ))}
          <Line type="monotone" dataKey="Overall" stroke={CHART_COLORS.blue} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4, fill: CHART_COLORS.blue, stroke: 'var(--color-dot-stroke)', strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
